import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { spanAt } from '@platform/contracts';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { MicroLabel } from '@/ui/micro-label';

/**
 * The strip under the video: what the operator was doing, at the moment they were doing it.
 *
 * A dataset's temporal annotations were readable only by downloading the file and reading JSON. The
 * viewer played a video with no idea what was in it, and the Episodes table claimed an annotation
 * depth it had not read. This is the other half: the L2 action spans drawn against the episode's
 * duration, the span under the playhead named, and the L3 body-part detail inside it named too.
 *
 * Positions are milliseconds from the start of the episode, and so is `video.currentTime` — the
 * preview is cut to the episode's own window at transcode time precisely so that these two are the
 * same clock and nothing here has to correct for an offset.
 */

export interface TimelineLevel3 {
  start_ms: number;
  end_ms: number;
  body_part: string;
  sub_action: string;
}

export interface TimelineSegment {
  start_ms: number;
  end_ms: number;
  action: string;
  confidence: number | null;
  level3: TimelineLevel3[];
}

export interface EpisodeTimelineData {
  caption: string;
  duration_ms: number;
  segments: TimelineSegment[];
}

const clock = (ms: number) => {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * The timeline for one episode, or nothing.
 *
 * A dataset with no sidecar answers 404, and that is not an error worth showing: most datasets have
 * no annotations, and a red panel on every one of them would be noise. `retry: false` keeps a
 * missing file from being asked for four times.
 */
export function useEpisodeTimeline(datasetId: string, episodeId: string | undefined, tenantId?: string) {
  return useQuery({
    queryKey: ['admin', 'episode-annotations', datasetId, episodeId],
    enabled: !!episodeId,
    retry: false,
    staleTime: 600_000,
    queryFn: () =>
      api<EpisodeTimelineData>(`/datasets/${datasetId}/episodes/${episodeId}/annotations`, { tenantId }).catch(
        error => {
          if (error instanceof ApiError && error.status === 404) return null;
          throw error;
        }
      )
  });
}

export function AnnotationTimeline({
  timeline,
  positionMs,
  onSeek
}: {
  timeline: EpisodeTimelineData;
  positionMs: number;
  /** Absent when there is no player to seek — the strip is then a picture, not a control. */
  onSeek?: (ms: number) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);

  /**
   * The duration the strip is drawn against.
   *
   * The annotations' own last end, not the video's, because the two are independent statements and
   * the strip has to be honest about the one it is drawing. They agree to within a frame on a
   * well-formed delivery; where they do not, scaling the spans to the video would silently stretch
   * them to fit.
   */
  const duration = Math.max(1, timeline.duration_ms);
  // Sorted server-side; asserted here because `spanAt` binary-searches and would otherwise be wrong
  // rather than slow.
  const segments = timeline.segments;
  const active = useMemo(() => spanAt(segments, positionMs), [segments, positionMs]);
  const activeSub = useMemo(() => (active ? spanAt(active.level3, positionMs) : null), [active, positionMs]);

  const seekTo = (clientX: number) => {
    const track = trackRef.current;
    if (!track || !onSeek) return;
    const box = track.getBoundingClientRect();
    if (box.width <= 0) return;
    const fraction = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
    onSeek(fraction * duration);
  };

  return (
    <div className="mt-3">
      <div
        ref={trackRef}
        // The track is a row of spans, so it is a group of buttons rather than a slider: clicking a
        // span jumps to it, and the gaps between spans are genuinely nothing to jump to.
        role={onSeek ? 'group' : undefined}
        aria-label={onSeek ? 'Annotation segments' : undefined}
        onClick={onSeek ? e => seekTo(e.clientX) : undefined}
        className={cn(
          'relative h-2.5 w-full overflow-hidden rounded-[3px] border border-border bg-inset',
          onSeek && 'cursor-pointer'
        )}
      >
        {segments.map((segment, index) => (
          <button
            key={`${segment.start_ms}-${index}`}
            type="button"
            tabIndex={onSeek ? 0 : -1}
            aria-label={`${clock(segment.start_ms)} ${segment.action}`}
            aria-current={segment === active}
            disabled={!onSeek}
            onClick={onSeek ? () => onSeek(segment.start_ms) : undefined}
            title={`${clock(segment.start_ms)} – ${clock(segment.end_ms)} · ${segment.action}`}
            className={cn(
              'absolute top-0 h-full border-0 border-r border-r-inset p-0',
              /**
               * Where you are is one channel; the fragmentation is another; nothing here encodes a
               * verdict. The spans are contiguous, so a single flat colour drew one long bar and
               * hid the very thing worth seeing — that the episode is twenty separate actions.
               *
               * Two shades of the same green alternating by position, plus a hairline at each
               * boundary. Parity carries no meaning at all; it exists so that neighbours differ,
               * which is what makes the boundary legible without inventing a palette. A colour per
               * action would be a rainbow and would imply a taxonomy the data does not have.
               */
              segment === active ? 'bg-primary' : index % 2 ? 'bg-pass/40' : 'bg-pass/70'
            )}
            style={{
              left: `${(segment.start_ms / duration) * 100}%`,
              // A half-second span over a sixteen-minute episode is 0.05% wide, which rounds to
              // nothing. A floor keeps every annotated moment visible on the strip.
              width: `max(2px, ${((segment.end_ms - segment.start_ms) / duration) * 100}%)`
            }}
          />
        ))}
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 h-full w-0.5 -translate-x-px bg-foreground"
          style={{ left: `${Math.min(100, Math.max(0, (positionMs / duration) * 100))}%` }}
        />
      </div>

      <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-mono text-[11px] text-muted">
          {clock(positionMs)} / {clock(duration)}
        </span>
        {active ? (
          <span className="text-[12px] text-foreground">{active.action}</span>
        ) : (
          // A real answer: the L2 timeline covers about 99.9% of an episode, and the playhead is in
          // one of its gaps. Saying nothing here would read as a component that had failed.
          <span className="text-[12px] text-dim">No action annotated here</span>
        )}
      </div>
      {activeSub ? (
        <MicroLabel size="xs" className="mt-1 block">
          {activeSub.body_part.replaceAll('_', ' ')} · {activeSub.sub_action}
        </MicroLabel>
      ) : null}
    </div>
  );
}
